import { isInteger, isNumber, isBoolean, defaults, get, find, map as map$2, pick, invokeMap, isArray, difference, omit, extend, forEach, includes, set, has, trim, toLower, castArray, zipWith, range, partition, reverse, uniqBy, some, filter, isString, differenceBy, transform } from 'lodash';
import { parse as parse$1 } from 'papaparse';
import { lookup, map, chain, combine, access, RawChangeSet, Field, DataType as DataType$1, idSymbol, simpleField, constant } from '@ngx-dino/core';
import { scaleLinear, scaleLog } from 'd3-scale';
import { __awaiter } from 'tslib';
import { defer, of } from 'rxjs';
import { map as map$1, delay } from 'rxjs/operators';
import { safeLoad, safeDump } from 'js-yaml';
import { nSQL } from 'nano-sql';

var DataType;
(function (DataType) {
    DataType["STRING"] = "string";
    DataType["NUMBER"] = "number";
    DataType["INTEGER"] = "integer";
    DataType["BOOLEAN"] = "boolean";
    DataType["DATE"] = "date";
    DataType["DATETIME"] = "datetime";
    DataType["UNKNOWN"] = "unknown";
})(DataType || (DataType = {}));
var ScaleType;
(function (ScaleType) {
    ScaleType["NOMINAL"] = "nominal";
    ScaleType["ORDINAL"] = "ordinal";
    ScaleType["INTERVAL"] = "interval";
    ScaleType["RATIO"] = "ratio";
    ScaleType["UNKNOWN"] = "unknown";
})(ScaleType || (ScaleType = {}));

var GraphicVariableType;
(function (GraphicVariableType) {
    GraphicVariableType["IDENTIFIER"] = "identifier";
    GraphicVariableType["TEXT"] = "text";
    GraphicVariableType["TOOLTIP"] = "tooltip";
    GraphicVariableType["AXIS"] = "axis";
    GraphicVariableType["INPUT"] = "input";
    GraphicVariableType["LABEL"] = "label";
    GraphicVariableType["LABEL_POSITION"] = "labelPosition";
    GraphicVariableType["FONT_SIZE"] = "fontSize";
    GraphicVariableType["ORDER"] = "order";
    GraphicVariableType["LATITUDE"] = "latitude";
    GraphicVariableType["LONGITUDE"] = "longitude";
    GraphicVariableType["X"] = "x";
    GraphicVariableType["Y"] = "y";
    GraphicVariableType["Z"] = "z";
    GraphicVariableType["SHAPE"] = "shape";
    GraphicVariableType["PULSE"] = "pulse";
    GraphicVariableType["HEIGHT"] = "height";
    GraphicVariableType["AREA_SIZE"] = "areaSize";
    GraphicVariableType["COLOR"] = "color";
    GraphicVariableType["TRANSPARENCY"] = "transparency";
    GraphicVariableType["STROKE_WIDTH"] = "strokeWidth";
    GraphicVariableType["STROKE_COLOR"] = "strokeColor";
    GraphicVariableType["STROKE_TRANSPARENCY"] = "strokeTransparency";
})(GraphicVariableType || (GraphicVariableType = {}));

function parse(input, config) {
    // Set dynamicTyping to false when our type parsing is implemented.
    const result = parse$1(input, config);
    const fields = result.meta.fields;
    if (fields) {
        const types = {};
        if (config.dynamicTyping) {
            // Add better type inferencing later
            result.data.forEach(item => {
                for (const key of fields) {
                    const value = item[key];
                    if (isInteger(value)) {
                        types[key] = DataType.INTEGER;
                    }
                    else if (isNumber(value)) {
                        types[key] = DataType.NUMBER;
                    }
                    else if (isBoolean(value)) {
                        types[key] = DataType.STRING;
                        item[key] = value ? 'true' : 'false';
                    }
                    else if (!types.hasOwnProperty(key)) {
                        types[key] = DataType.STRING;
                    }
                }
            });
        }
        result.meta.typings = fields.map(f => types[f] || DataType.STRING);
    }
    return result;
}

/**
 * A decorator that allows a property to store data, but not be written to JSON
 */
function Transient(target, key) {
    if (delete target[key]) {
        const cacheKey = `__HIDDEN_${key}__`;
        Object.defineProperty(target, key, {
            get: function () {
                return this[cacheKey];
            },
            set: function (value) {
                if (this.hasOwnProperty(cacheKey)) {
                    this[cacheKey] = value;
                }
                else {
                    Object.defineProperty(this, cacheKey, {
                        configurable: false,
                        enumerable: false,
                        writable: true,
                        value
                    });
                }
            },
            enumerable: false,
            configurable: false,
        });
    }
}

function byteToString(byte) {
    const value = Math.round(byte).toString(16);
    return '0'.repeat(2 - value.length) + value;
}
class ColorScale {
    constructor(start, end, outOfDomainColor, strokeOffset = 0) {
        this.outOfDomainColor = outOfDomainColor;
        this.strokeOffset = strokeOffset;
        this.startRGB = this.getRGB(start);
        this.endRGB = this.getRGB(end);
    }
    qualitative(items, strokeOffset = 0) {
        const colors = new Map();
        const colorer = this.getQualitativeColorFunction([0, items.length - 1], strokeOffset);
        items.forEach((item, index) => {
            colors.set(item, colorer(index));
        });
        return lookup(colors, this.outOfDomainColor);
    }
    qualitativeStrokeColor(items) {
        return this.qualitative(items, this.strokeOffset);
    }
    getQualitativeColorFunction(domain, offset = 0) {
        const addends = this.getAddends(domain[1] - domain[0]);
        return (index) => this.getHex({
            r: Math.min(255, Math.max(0, this.startRGB.r + addends[0] * index + offset)),
            g: Math.min(255, Math.max(0, this.startRGB.g + addends[1] * index + offset)),
            b: Math.min(255, Math.max(0, this.startRGB.b + addends[2] * index + offset))
        });
    }
    quantitative(domain, strokeOffset = 0) {
        return map(this.getQuantitativeColorFunction(domain, strokeOffset));
    }
    quantitativeStrokeColor(domain) {
        return this.quantitative(domain, this.strokeOffset);
    }
    getQuantitativeColorFunction(domain, offset = 0) {
        return scaleLinear()
            .domain(domain)
            .range([
            this.getHex({
                r: Math.min(255, Math.max(0, this.startRGB.r + offset)),
                g: Math.min(255, Math.max(0, this.startRGB.g + offset)),
                b: Math.min(255, Math.max(0, this.startRGB.b + offset))
            }),
            this.getHex({
                r: Math.min(255, Math.max(0, this.endRGB.r + offset)),
                g: Math.min(255, Math.max(0, this.endRGB.g + offset)),
                b: Math.min(255, Math.max(0, this.endRGB.b + offset))
            })
        ]);
    }
    getRGB(hex) {
        hex = hex.substring(1);
        const color = { r: 0, g: 0, b: 0 };
        color.r = parseInt('0x' + hex.substring(0, 2), 16);
        color.g = parseInt('0x' + hex.substring(2, 4), 16);
        color.b = parseInt('0x' + hex.substring(4, 6), 16);
        return color;
    }
    getHex(color) {
        const { r, g, b } = color;
        return `#${byteToString(r)}${byteToString(g)}${byteToString(b)}`;
    }
    getAddends(categoryCount) {
        return Array.of((this.endRGB.r - this.startRGB.r), (this.endRGB.g - this.startRGB.g), (this.endRGB.b - this.startRGB.b)).map((a) => a / categoryCount);
    }
}

class SizeScale {
    constructor(start, end, outOfDomainSize, quantitativeScaleType = 'linear') {
        this.start = start;
        this.end = end;
        this.outOfDomainSize = outOfDomainSize;
        this.quantitativeScaleType = quantitativeScaleType;
    }
    qualitative(items) {
        const sizes = new Map();
        const sizeFunction = this.getQualitativeSizeFunction([0, items.length - 1]);
        items.forEach((item, index) => {
            sizes.set(item, sizeFunction(index));
        });
        return lookup(sizes, this.outOfDomainSize);
    }
    getQualitativeSizeFunction(domain) {
        const addend = (this.end - this.start) / (domain[1] - domain[0]);
        return (index) => this.start + index * addend;
    }
    quantitative(domain) {
        return map(this.getQuantitativeSizeFunction(domain));
    }
    getQuantitativeSizeFunction(domain) {
        switch (this.quantitativeScaleType) {
            default:
            case 'linear': return scaleLinear()
                .domain(domain).range([this.start, this.end]);
            case 'log':
                if (domain[0] === 0) {
                    domain[0] = 1e-6;
                }
                return scaleLog()
                    .domain(domain).range([this.start, this.end]);
        }
    }
}

function norm0to100(field, maxField, minField) {
    if (!minField) {
        return chain(combine([access(field, 0), access(maxField, 1)]), map(([val, maxVal]) => val / maxVal * 100));
    }
    else {
        return chain(combine([access(field, 0), access(maxField, 1), access(minField, 0)]), map(([val, maxVal, minVal]) => (val - minVal) / (maxVal - minVal) * 100));
    }
}
function extractPoint(...fields) {
    return chain(combine(fields.map(f => access(f, undefined))), 
    // If any data wasn't mapped, return undefined
    map(data => data.some(d => d === undefined) ? undefined : data));
}
const ɵ0 = x => x.toLocaleString();
const formatNumber = map(ɵ0);
const ɵ1 = x => '' + x;
const formatYear = map(ɵ1);
// Material colors: Teal 800 (dark) to Teal 50 (light)
const colorScale = new ColorScale('#00695c', '#e0f2f1', '#ffffff', -51);
const colorScaleNormQuantitative = colorScale.quantitative([0, 100]);
const colorScaleNormQuantitativeStroke = colorScale.quantitativeStrokeColor([0, 100]);
// Same color scale, but reversed Light to Dark
const colorScaleReversedNormQuantitative = colorScale.quantitative([100, 0]);
const colorScaleReversedNormQuantitativeStroke = colorScale.quantitativeStrokeColor([100, 0]);
const greyScale = new ColorScale('#bdbdbd', '#000000', '#ffffff', -51);
const greyScaleNormQuantitative = greyScale.quantitative([0, 100]);
const greyScaleNormQuantitativeStroke = greyScale.quantitativeStrokeColor([0, 100]);
const areaSizeRange = [28, 750];
const areaSizeScale = new SizeScale(areaSizeRange[0], areaSizeRange[1], 5, 'linear'); // TBD TODO
const areaSizeScaleNormQuantitative = areaSizeScale.quantitative([0, 100]);
const fontSizeRange = [6, 20];
const fontSizeScale = new SizeScale(fontSizeRange[0], fontSizeRange[1], 5, 'linear'); // TBD TODO
const fontSizeScaleNormQuantitative = fontSizeScale.quantitative([0, 100]);
const strokeWidthRange = [0.5, 8];
const strokeWidthScale = new SizeScale(strokeWidthRange[0], strokeWidthRange[1], 5, 'linear'); // TBD TODO
const strokeWidthScaleNormQuantitative = strokeWidthScale.quantitative([0, 100]);
const ɵ2 = n => ((100 - n) / 100) * 0.49;
const quantitativeTransparency = map(ɵ2);
const defaultStyles = {
    transparency: 0.25,
    strokeTransparency: 0.0,
    strokeWidth: 0.5,
    strokeColor: '#000007',
    labelPosition: 'left',
    labelPositionCenter: 'center'
};

class DefaultRecordStream {
    constructor(data, rawData) {
        this.rawData = rawData;
        Object.assign(this, data);
        defaults(this, { label: data.id });
    }
    asObservable() {
        return defer(() => this.getData()).pipe(map$1(RawChangeSet.fromArray), 
        // A delay was added to allow for pagination to initialize properly for async data
        delay(10));
    }
    getData() {
        return __awaiter(this, void 0, void 0, function* () {
            return get(yield this.rawData.getData(), this.id, []);
        });
    }
    toJSON() {
        return { id: this.id, label: this.label };
    }
}

class DefaultDataSource {
    constructor(data, project) {
        this.template = 'default';
        const rawData = find(project.rawData, ['id', data.properties.rawData]);
        Object.assign(this, data, {
            recordStreams: map$2(data.recordStreams, arg => new DefaultRecordStream(arg, rawData))
        });
    }
    toJSON() {
        return Object.assign(pick(this, ['id', 'template', 'properties']), {
            recordStreams: invokeMap(this.recordStreams, 'toJSON')
        });
    }
}
class DefaultDataSourceFactory {
    constructor() {
        this.id = 'default';
        this.type = 'dataSource';
    }
    fromJSON(data, context, registry) {
        return __awaiter(this, void 0, void 0, function* () {
            if (data.template !== this.id && registry.hasObjectFactory('dataSource', data.template)) {
                return yield registry.fromJSON('dataSource', data.template, data, context);
            }
            return new DefaultDataSource(data, context);
        });
    }
    toJSON(instance, _context, _registry) {
        return instance.toJSON();
    }
}

class DefaultDataVariable {
    constructor(data, recordSet) {
        this.recordSet = recordSet;
        Object.assign(this, data);
    }
    toJSON() {
        return pick(this, ['id', 'label', 'dataType', 'scaleType']);
    }
}

class DefaultGraphicSymbol {
    constructor(data, project) {
        this.id = data.id;
        this.type = data.type;
        this.recordStream = project.getRecordStream(data.recordStream);
        this.graphicVariables = {};
        for (const [id, gvarData] of Object.entries(data.graphicVariables)) {
            const recordSet = project.recordSets.find((rs) => rs.id === gvarData['recordSet']);
            if (!recordSet) {
                throw new Error(`${id}: RecordSet => ${gvarData['recordSet']} not found.`);
            }
            const gvar = {
                recordStream: this.recordStream,
                recordSet,
                dataVariable: recordSet.dataVariables.find((dv) => dv.id === gvarData['dataVariable']),
                type: gvarData['graphicVariableType'],
                id: gvarData['graphicVariableId']
            };
            const matches = project.findObjects(project.graphicVariables, gvar);
            if (matches.length > 0) {
                this.graphicVariables[id] = matches[0];
            }
            else {
                throw new Error(`${id}: ${this.recordStream.id} => ${JSON.stringify(gvarData, null, 2)} not found in graphicVariables`);
            }
        }
    }
    toJSON() {
        const graphicVariables = {};
        for (const [id, gvar] of Object.entries(this.graphicVariables)) {
            graphicVariables[id] = {
                recordSet: gvar.recordSet.id,
                dataVariable: gvar.dataVariable.id,
                graphicVariableType: gvar.type,
                graphicVariableId: gvar.id
            };
        }
        return {
            id: this.id,
            type: this.type,
            recordStream: this.recordStream.id,
            graphicVariables
        };
    }
}
class DefaultGraphicSymbolFactory {
    constructor() {
        this.id = 'default';
        this.type = 'graphicSymbolMappings';
    }
    fromJSON(data, context, _registry) {
        return new DefaultGraphicSymbol(data, context);
    }
    toJSON(instance, _context, _registry) {
        return instance.toJSON();
    }
}

class DefaultGraphicVariable {
    constructor(data) {
        Object.assign(this, data);
    }
    asBoundField() {
        return new Field({
            id: this.id,
            label: this.label,
            dataType: this.getDataType(),
            mapping: { [Field.defaultSymbol]: access(this.selector) }
        }).getBoundField();
    }
    toJSON() {
        return Object.assign({}, this, {
            recordStream: this.recordStream.id,
            recordSet: this.recordSet.id,
            dataVariable: this.dataVariable.id
        });
    }
    getDataType() {
        // TODO: This mapping should be smarter/revised.
        switch (this.dataVariable.dataType) {
            case 'integer':
            case DataType$1.Number:
                return DataType$1.Number;
            case DataType$1.Boolean:
                return DataType$1.Boolean;
            default:
                return DataType$1.String;
        }
    }
}
class DefaultGraphicVariableMapping {
    static fromJSON(allData, project) {
        const variables = [];
        for (const data of allData) {
            const recordStream = project.getRecordStream(data.recordStream);
            for (const [recordSetId, dvMapping] of Object.entries(data.mappings)) {
                for (const [dataVariableId, gvMapping] of Object.entries(dvMapping)) {
                    for (const [type, gvDatas] of Object.entries(gvMapping)) {
                        const recordSet = project.recordSets.find(rs => rs.id === recordSetId);
                        const dataVariable = recordSet.dataVariables.find(dv => dv.id === dataVariableId);
                        const gvDataArray = gvDatas;
                        for (const gvData of gvDataArray) {
                            if (!gvData.id && gvDataArray.length === 1) {
                                gvData.id = type;
                            }
                            variables.push(new DefaultGraphicVariable({
                                id: gvData.id, label: gvData.label || dataVariable.label || gvData.id, type, selector: gvData.selector,
                                recordStream, recordSet, dataVariable
                            }));
                        }
                    }
                }
            }
        }
        return variables;
    }
    static toJSON(graphicVariables) {
        const mappings = {};
        for (const gvar of graphicVariables) {
            const recordStream = gvar.recordStream.id;
            const recordSet = gvar.recordSet.id;
            const dataVariable = gvar.dataVariable.id;
            const type = gvar.type;
            if (!mappings.hasOwnProperty(recordStream)) {
                mappings[recordStream] = {};
            }
            if (!mappings[recordStream].hasOwnProperty(recordSet)) {
                mappings[recordStream][recordSet] = {};
            }
            if (!mappings[recordStream][recordSet].hasOwnProperty(dataVariable)) {
                mappings[recordStream][recordSet][dataVariable] = {};
            }
            if (!mappings[recordStream][recordSet][dataVariable].hasOwnProperty(type)) {
                mappings[recordStream][recordSet][dataVariable][type] = [];
            }
            mappings[recordStream][recordSet][dataVariable][type].push({
                id: gvar.id,
                label: gvar.label || gvar.id,
                selector: gvar.selector
            });
        }
        return Object.entries(mappings).map(([recordStream, rsMapping]) => {
            return { recordStream, mappings: rsMapping };
        });
    }
}
class DefaultGraphicVariableMappingFactory {
    constructor() {
        this.id = 'default';
        this.type = 'graphicVariableMappings';
    }
    fromJSON(data, context, _registry) {
        return DefaultGraphicVariableMapping.fromJSON(data, context);
    }
    toJSON(instance, _context, _registry) {
        return DefaultGraphicVariableMapping.toJSON(instance);
    }
}

class DefaultProject {
    constructor(data = {}) {
        this.apiVersion = '1';
        this.metadata = { dateCreated: new Date() };
        this.dataSources = [];
        this.recordSets = [];
        this.graphicVariables = [];
        this.graphicSymbols = [];
        this.visualizations = [];
        this.rawData = [];
        Object.assign(this, data);
    }
    getRecordStream(id) {
        for (const dataSource of this.dataSources) {
            for (const stream of dataSource.recordStreams) {
                if (stream.id === id) {
                    return stream;
                }
            }
        }
        return null;
    }
    findObjects(data, search) {
        return data.filter((d) => {
            for (const key in search) {
                if (search[key] !== d[key]) {
                    return false;
                }
            }
            return true;
        });
    }
}
class DefaultProjectFactory {
    constructor() {
        this.id = 'default';
        this.type = 'project';
    }
    fromJSON(data, context, registry) {
        return __awaiter(this, void 0, void 0, function* () {
            const p = context = new DefaultProject();
            // Make sure raw data is parsed first
            p.rawData = yield registry.fromJSONArray('rawData', 'default', data.rawData, context);
            p.metadata = data.metadata || undefined;
            p.dataSources = yield registry.fromJSONArray('dataSource', 'default', data.dataSources, context);
            p.recordSets = yield registry.fromJSONArray('recordSet', 'default', data.recordSets, context);
            p.recordSets.forEach(rs => rs.resolveParent(p.recordSets));
            p.graphicVariables =
                yield registry.fromJSON('graphicVariableMappings', 'default', data.graphicVariableMappings, context);
            p.graphicSymbols =
                yield registry.fromJSONArray('graphicSymbolMappings', 'default', data.graphicSymbolMappings, context);
            p.visualizations = yield registry.fromJSONArray('visualization', 'default', data.visualizations, context);
            return p;
        });
    }
    toJSON(instance, _context, registry) {
        return __awaiter(this, void 0, void 0, function* () {
            instance.metadata.dateSaved = new Date();
            const data = {
                apiVersion: 1,
                metadata: instance.metadata,
                dataSources: yield registry.toJSONArray('dataSource', 'default', instance.dataSources, instance),
                recordSets: yield registry.toJSONArray('recordSet', 'default', instance.recordSets, instance),
                graphicVariableMappings: yield registry.toJSON('graphicVariableMappings', 'default', instance.graphicVariables, instance),
                graphicSymbolMappings: yield registry.toJSONArray('graphicSymbolMappings', 'default', instance.graphicSymbols, instance),
                visualizations: yield registry.toJSONArray('visualization', 'default', instance.visualizations, instance),
                rawData: yield registry.toJSONArray('rawData', 'default', instance.rawData, instance),
            };
            return data;
        });
    }
}

class DefaultRawData {
    constructor(data) {
        this.template = 'default';
        Object.assign(this, data);
    }
    setData(data) {
        this.data = data;
        this.url = undefined;
        this._parsed_data_ = undefined;
    }
    getData() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._parsed_data_) {
                if (this.data && this.template === 'csv') {
                    this._parsed_data_ = this.getCSVData(this.data);
                }
                else if (this.data) {
                    this._parsed_data_ = this.asObjectStore(this.data);
                }
                else if (this.url) {
                    this._parsed_data_ = this.getRemoteData();
                }
            }
            return yield this._parsed_data_;
        });
    }
    getRemoteData() {
        return __awaiter(this, void 0, void 0, function* () {
            const text = yield (yield fetch(this.url)).text();
            if (this.template === 'string') {
                return text;
            }
            else if (this.template === 'csv') {
                return this.getCSVData(text);
            }
            else {
                // Assumes either JSON or YAML
                return this.asObjectStore(safeLoad(text));
            }
        });
    }
    getCSVData(text) {
        const parseResults = parse(text, { header: true, dynamicTyping: true, skipEmptyLines: true });
        return this.asObjectStore(parseResults.data);
    }
    asObjectStore(data) {
        if (isArray(data)) {
            const results = {};
            // The data is stored in a property with the same string as the id
            results[this.id] = data;
            return results;
        }
        else {
            return data;
        }
    }
    toJSON() {
        return { id: this.id, template: this.template, data: this.data, url: this.url };
    }
}
class DefaultRawDataFactory {
    constructor() {
        this.id = 'default';
        this.type = 'rawData';
    }
    fromJSON(data, context, registry) {
        if (data.template !== this.id && registry.hasObjectFactory('rawData', data.template)) {
            return registry.fromJSON('rawData', data.template, data, context);
        }
        return new DefaultRawData(data);
    }
    toJSON(instance, _context, _registry) {
        return instance.toJSON();
    }
}

class DefaultRecordSet {
    constructor(data, project) {
        Object.assign(this, data, {
            parent: undefined,
            parentString: data.parent,
            defaultRecordStream: project.getRecordStream(data.defaultRecordStream),
            dataVariables: map$2(data.dataVariables, arg => new DefaultDataVariable(arg, this))
        });
    }
    resolveParent(recordSets) {
        if (this.parentString && recordSets) {
            this.parent = find(recordSets, ['id', this.parentString]);
            this.parentString = undefined;
        }
    }
    toJSON() {
        return Object.assign(pick(this, ['id', 'label', 'labelPlural', 'description']), {
            parent: get(this.parent, 'id'),
            defaultRecordStream: get(this.defaultRecordStream, 'id'),
            dataVariables: invokeMap(this.dataVariables, 'toJSON')
        });
    }
}
class DefaultRecordSetFactory {
    constructor() {
        this.id = 'default';
        this.type = 'recordSet';
    }
    fromJSON(data, context, _registry) {
        return new DefaultRecordSet(data, context);
    }
    toJSON(instance, _context, _registry) {
        return instance.toJSON();
    }
}

class DefaultVisualization {
    constructor(data, project) {
        this.template = 'default';
        const graphicSymbols = {};
        for (const [slot, symbolId] of Object.entries(data.graphicSymbols)) {
            const matches = project.findObjects(project.graphicSymbols, { id: symbolId });
            if (matches.length > 0) {
                graphicSymbols[slot] = matches[0];
            }
            else {
                throw new Error(`${slot}: ${symbolId} not found in graphicSymbols`);
            }
        }
        Object.assign(this, data, { graphicSymbols });
    }
    toJSON() {
        const graphicSymbols = {};
        for (const [slot, symbol] of Object.entries(this.graphicSymbols)) {
            graphicSymbols[slot] = symbol.id;
        }
        return {
            id: this.id,
            description: this.description,
            template: this.template,
            properties: this.properties,
            graphicSymbols
        };
    }
}
class DefaultVisualizationFactory {
    constructor() {
        this.id = 'default';
        this.type = 'visualization';
    }
    fromJSON(data, context, registry) {
        return __awaiter(this, void 0, void 0, function* () {
            if (registry.hasObjectFactory('visualization', data.template)) {
                return registry.fromJSON('visualization', data.template, data, context);
            }
            return new DefaultVisualization(data, context);
        });
    }
    toJSON(instance, _context, _registry) {
        return instance.toJSON();
    }
}

class DefaultPlugin {
    register(registry) {
        registry.registerObjectFactory(new DefaultProjectFactory());
        registry.registerObjectFactory(new DefaultDataSourceFactory());
        registry.registerObjectFactory(new DefaultRecordSetFactory());
        registry.registerObjectFactory(new DefaultGraphicVariableMappingFactory());
        registry.registerObjectFactory(new DefaultGraphicSymbolFactory());
        registry.registerObjectFactory(new DefaultVisualizationFactory());
        registry.registerObjectFactory(new DefaultRawDataFactory());
    }
}

class ActivityLogRawData {
    constructor(id, data) {
        this.id = id;
        this.template = 'activityLog';
        this.saveActivityLog = true;
        ActivityLogRawData.db = this.setupDB(data.data);
    }
    setupDB(data) {
        return __awaiter(this, void 0, void 0, function* () {
            if (ActivityLogRawData.db) {
                const db = (yield ActivityLogRawData.db);
                yield db.query('drop').exec();
            }
            yield nSQL('activitylog')
                .model(ActivityLogRawData.schema).actions([{
                    name: 'add_new_log',
                    args: ['activitylog:map'],
                    call: function (args, database) {
                        return database.query('upsert', args.activitylog).exec();
                    }
                }
            ]).config({
                mode: 'TEMP'
            }).connect();
            if (data && data.activityLog) {
                const activityLog = data.activityLog.map(this.mapActivityLog);
                yield nSQL('activitylog').loadJS('activitylog', activityLog);
            }
            return ActivityLogRawData.db = nSQL('activitylog');
        });
    }
    mapActivityLog(activityLog) {
        const nonPayloadKeys = ActivityLogRawData.schema.map(a => a.key).filter(b => b !== 'payload');
        const payloadKeys = difference(Object.keys(activityLog), nonPayloadKeys);
        const payload = pick(activityLog, payloadKeys);
        const nonPayloadObject = omit(activityLog, payloadKeys);
        return extend(nonPayloadObject, { 'payload': JSON.stringify(payload) });
    }
    logActivity(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            (yield ActivityLogRawData.db).doAction('add_new_log', {
                activitylog: {
                    id: null,
                    actionName: get(msg, 'logData.data.type'),
                    date: new Date().toLocaleString(),
                    time: new Date().getTime(),
                    payload: JSON.stringify(omit(msg.logData.data.payload, ['project']))
                }
            });
        });
    }
    getData() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.saveActivityLog) {
                return (yield ActivityLogRawData.db).query('select').exec().then((rows) => {
                    rows = map$2(rows, logItem => {
                        const removePayload = omit(logItem, ['payload']);
                        const parsedPayload = JSON.parse(logItem['payload']);
                        return extend(removePayload, parsedPayload);
                    });
                    return { activityLog: rows };
                });
            }
            else {
                return { activityLog: [] };
            }
        });
    }
    toJSON() {
        return __awaiter(this, void 0, void 0, function* () {
            const logData = yield this.getData();
            return { id: this.id, template: this.template, data: logData };
        });
    }
}
ActivityLogRawData.db = null;
ActivityLogRawData.schema = [
    { key: 'logid', type: 'int', props: ['pk', 'ai'] },
    { key: 'actionName', type: 'string' },
    { key: 'date', type: 'string' },
    { key: 'payload', type: 'string' },
    { key: 'time', type: 'int' }
];
class ActivityLogRawDataFactory {
    constructor() {
        this.id = 'activityLog';
        this.type = 'rawData';
    }
    fromJSON(data, _context, _registry) {
        return __awaiter(this, void 0, void 0, function* () {
            return new ActivityLogRawData(data.id, data);
        });
    }
    toJSON(instance, _context, _registry) {
        return __awaiter(this, void 0, void 0, function* () {
            return instance.toJSON();
        });
    }
}

class ActivityLogDataSource {
    constructor(data, project) {
        this.template = 'activityLog';
        let rawData = find(project.rawData, ['id', data.properties.rawData]);
        if (!rawData) {
            rawData = new ActivityLogRawData('activityLog', data);
            project.rawData.push(rawData);
        }
        Object.assign(this, data, {
            recordStreams: map$2(data.recordStreams, arg => new DefaultRecordStream(arg, rawData))
        });
    }
    toJSON() {
        return Object.assign(pick(this, ['id', 'template', 'properties']), {
            recordStreams: invokeMap(this.recordStreams, 'toJSON')
        });
    }
}
class ActivityLogDataSourceFactory {
    constructor() {
        this.id = 'activityLog';
        this.type = 'dataSource';
    }
    fromJSON(data, context, _registry) {
        return __awaiter(this, void 0, void 0, function* () {
            return new ActivityLogDataSource(data, context);
        });
    }
    toJSON(instance, _context, _registry) {
        return instance.toJSON();
    }
}

class ActivityLogPlugin {
    register(registry) {
        registry.registerObjectFactory(new ActivityLogDataSourceFactory());
        registry.registerObjectFactory(new ActivityLogRawDataFactory());
    }
}

class ObjectFactoryRegistry {
    constructor(plugins = []) {
        this.plugins = [];
        this.factories = {};
        forEach(plugins, plugin => this.registerPlugin(plugin));
    }
    registerPlugin(plugin) {
        if (!includes(this.plugins, plugin)) {
            this.plugins.push(plugin);
            plugin.register(this);
        }
    }
    registerObjectFactory(objectFactory) {
        set(this.factories, [objectFactory.type, objectFactory.id], objectFactory);
    }
    getObjectFactory(objectType, template) {
        const factory = get(this.factories, [objectType, template]);
        if (factory !== undefined) {
            return factory;
        }
        throw new Error(`${template} object factory of type: ${objectType} does not exist.`);
    }
    hasObjectFactory(objectType, template) {
        return has(this.factories, [objectType, template]);
    }
    fromJSONArray(objectType, template, data, context) {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = map$2(data, item => this.fromJSON(objectType, template, item, context));
            return Promise.all(promises);
        });
    }
    toJSONArray(objectType, template, instances, context) {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = map$2(instances, instance => this.toJSON(objectType, template, instance, context));
            return Promise.all(promises);
        });
    }
    fromJSON(objectType, template, data, context) {
        return __awaiter(this, void 0, void 0, function* () {
            const factory = this.getObjectFactory(objectType, template);
            return factory.fromJSON(data, context, this);
        });
    }
    toJSON(objectType, template, instance, context) {
        return __awaiter(this, void 0, void 0, function* () {
            const factory = this.getObjectFactory(objectType, template);
            return factory.toJSON(instance, context, this);
        });
    }
}

class NormalizedField {
    constructor(field, dataType, columnNumber) {
        this.field = field;
        this.dataType = dataType;
        this.variableType = undefined;
        const normalized = this.key = trim(toLower(field));
        if (normalized === '') {
            this.field = `Column ${columnNumber}`;
            this.key = toLower(this.field);
        }
        const index = normalized.indexOf(NormalizedField.splitSequence);
        if (index !== -1) {
            const type = normalized.slice(index + NormalizedField.splitSequence.length);
            this.key = normalized.slice(0, index);
            this.variableType = find(GraphicVariableType, value => toLower(value) === type);
        }
    }
    get fullKey() {
        const suffix = NormalizedField.splitSequence + this.variableType;
        return this.key + (this.variableType ? suffix : '');
    }
    get selector() { return this.field; }
    get selectorObj() { return { selector: this.selector }; }
    get isMapping() { return this.variableType !== undefined; }
}
NormalizedField.splitSequence = '$$';
class CSVTemplateProject extends DefaultProject {
    static create(csvFileContents, fileNames) {
        return __awaiter(this, void 0, void 0, function* () {
            const project = new CSVTemplateProject(csvFileContents, fileNames);
            yield project.prePopulateData();
            return project;
        });
    }
    constructor(csvFileContents, fileNames) {
        super();
        const names = castArray(fileNames);
        const contents = castArray(csvFileContents);
        for (let index = 0; index < contents.length; index++) {
            const suffixer = value => `${value}${index + 1}`;
            const name = names[index] || '';
            const parsed = this.getParsedData(contents[index]);
            // TODO put errors in log
            const [regularFields, mappingFields] = this.normalizeFields(parsed.meta);
            // Order is very important here!
            this.rawData.push(this.createRawData(contents[index], suffixer));
            this.dataSources.push(this.createDataSource(suffixer));
            this.recordSets.push(this.createRecordSet(name, regularFields, suffixer));
            this.graphicVariables.push(...this.createGraphicVariables(regularFields, mappingFields, suffixer));
        }
        this.dataSources.push(new ActivityLogDataSource({
            id: 'activityLog',
            properties: { rawData: 'activityLog', keepPreviousActivity: true, freezeLogs: false },
            recordStreams: [{ id: 'activityLog', label: 'Activity Log' }]
        }, this));
    }
    prePopulateData() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all(this.rawData.map(r => r.getData()));
        });
    }
    getParsedData(raw) {
        return parse(raw, {
            header: true,
            dynamicTyping: true,
            skipEmptyLines: true
        });
    }
    normalizeFields(meta) {
        const normalized = zipWith(meta.fields, meta.typings, range(meta.fields.length), (field, dataType, columnNumber) => new NormalizedField(field, dataType, columnNumber));
        const [mapping, regular] = partition(normalized, 'isMapping');
        const regularUniq = reverse(uniqBy(reverse(regular), 'key'));
        const mappingUniq = reverse(uniqBy(reverse(mapping), 'fullKey'));
        // Add missing regular fields
        for (const field of mappingUniq) {
            if (!some(regularUniq, ['key', field.key])) {
                regularUniq.push(new NormalizedField(field.key, DataType.UNKNOWN, 0));
            }
        }
        return [regularUniq, mappingUniq];
    }
    createRawData(csvData, suffixer) {
        return new DefaultRawData({
            id: suffixer('csvData'),
            template: 'csv',
            data: csvData
        });
    }
    createDataSource(suffixer) {
        return new DefaultDataSource({
            id: suffixer('csvDataSource'),
            properties: {
                rawData: suffixer('csvData')
            },
            recordStreams: [{
                    id: suffixer('csvData'),
                    label: suffixer('CSV Data ')
                }]
        }, this);
    }
    createDataVariableArgs(field) {
        return {
            id: field.selector,
            label: field.selector,
            dataType: field.dataType,
            scaleType: '???'
        };
    }
    createRecordSet(name, fields, suffixer) {
        return new DefaultRecordSet({
            id: suffixer('csvData'),
            label: suffixer('CSV Data '),
            labelPlural: suffixer('CSV Data '),
            description: name,
            defaultRecordStream: suffixer('csvData'),
            dataVariables: map$2(fields, this.createDataVariableArgs)
        }, this);
    }
    createMapping(field, mappingFields) {
        const defaultSelector = () => [field.selectorObj];
        const mapping = {
            [GraphicVariableType.IDENTIFIER]: defaultSelector(),
            [GraphicVariableType.AXIS]: defaultSelector(),
            [GraphicVariableType.TEXT]: defaultSelector(),
            [GraphicVariableType.TOOLTIP]: defaultSelector(),
            [GraphicVariableType.LABEL]: defaultSelector(),
            [GraphicVariableType.INPUT]: defaultSelector(),
            [GraphicVariableType.ORDER]: defaultSelector()
        };
        // The following are guesses and likely not correct
        if (field.dataType === DataType.NUMBER || field.dataType === DataType.INTEGER) {
            mapping[GraphicVariableType.AREA_SIZE] = defaultSelector();
            mapping[GraphicVariableType.STROKE_WIDTH] = defaultSelector();
            mapping[GraphicVariableType.FONT_SIZE] = defaultSelector();
        }
        if (field.dataType === DataType.STRING && includes(field.key, 'color')) {
            mapping[GraphicVariableType.COLOR] = defaultSelector();
            mapping[GraphicVariableType.STROKE_COLOR] = defaultSelector();
        }
        // Use the field name as a graphic variable type.
        // Use Case: User formats their csv to have color in color column, etc.
        const type = find(GraphicVariableType, value => toLower(value) === field.key);
        if (type) {
            mapping[type] = defaultSelector();
        }
        // Override with predefined mappings
        for (const mappingField of filter(mappingFields, ['key', field.key])) {
            mapping[mappingField.variableType] = [mappingField.selectorObj];
        }
        return mapping;
    }
    createGraphicVariables(regularFields, mappingFields, suffixer) {
        const variables = {};
        for (const field of regularFields) {
            variables[field.selector] = this.createMapping(field, mappingFields);
        }
        return DefaultGraphicVariableMapping.fromJSON([{
                recordStream: suffixer('csvData'),
                mappings: {
                    [suffixer('csvData')]: variables
                }
            }], this);
    }
}

class GraphicSymbolData {
    constructor(dataProcessorCreator, visualization, graphicSymbol, defaultValues = {}, postprocessingFields = {}) {
        this.dataProcessorCreator = dataProcessorCreator;
        this.visualization = visualization;
        this.defaultValues = defaultValues;
        this.postprocessingFields = postprocessingFields;
        this.requiredFields = ['identifier'];
        if (isString(graphicSymbol)) {
            this.graphicSymbol = visualization.graphicSymbols[graphicSymbol];
            if (!this.graphicSymbol) {
                throw new Error(`'Graphic Symbol ${graphicSymbol} not found in Visualization ${visualization.id}`);
            }
        }
        else {
            this.graphicSymbol = graphicSymbol;
        }
        const options = visualization.graphicSymbolOptions.filter(gso => gso.id === this.graphicSymbol.id);
        if (options.length) {
            this.requiredFields = options[0].graphicVariableOptions.filter(gvo => gvo['required'] === true).map(gvo => gvo.id || gvo.type);
        }
        if (this.requiredFields.indexOf('identifier') === -1) {
            this.requiredFields.push('identifier');
        }
    }
    asRawChangeSet() {
        const gvNames = Object.keys(this.graphicSymbol.graphicVariables);
        const inValid = this.requiredFields.some((f) => gvNames.indexOf(f) === -1);
        if (inValid) {
            return of(RawChangeSet.fromArray([]));
        }
        else {
            return this.graphicSymbol.recordStream.asObservable();
        }
    }
    asChangeSet() {
        return this.createProcessor().asObservable();
    }
    asDataArray() {
        let data = [];
        return this.asChangeSet().pipe(map$1((set) => {
            data = this.applyChangeSet(set, data);
            return data;
        }));
    }
    createProcessor() {
        const { graphicSymbol, defaultValues } = this;
        return this.dataProcessorCreator.createProcessor(this.asRawChangeSet(), graphicSymbol.graphicVariables['identifier'].asBoundField(), this.graphicSymbolBoundFields(graphicSymbol, defaultValues), this.postprocessingFields, { strictMode: false, keepAlive: true });
    }
    applyChangeSet(set, data) {
        const result = differenceBy(data, set.remove.toArray(), set.replace.toArray(), idSymbol);
        set.insert.forEach(item => result.push(item));
        set.replace.forEach(item => result.push(item));
        return result;
    }
    graphicSymbolBoundFields(graphicSymbol, defaults = {}) {
        return transform(graphicSymbol.graphicVariables, (result, gv, k) => {
            result[k] = gv.asBoundField();
        }, transform(defaults, (results, v, k) => {
            results[k] = simpleField({
                label: k,
                bfieldId: k,
                operator: constant(v)
            }).getBoundField();
        }));
    }
}

class ProjectSerializer {
    static toJSON(project, registry = ProjectSerializer.defaultRegistry) {
        return __awaiter(this, void 0, void 0, function* () {
            return registry.toJSON('project', 'default', project, project);
        });
    }
    static fromJSON(data, registry = ProjectSerializer.defaultRegistry) {
        return __awaiter(this, void 0, void 0, function* () {
            return registry.fromJSON('project', 'default', data);
        });
    }
    static toYAML(project, registry = ProjectSerializer.defaultRegistry) {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield ProjectSerializer.toJSON(project, registry);
            return safeDump(data, { skipInvalid: true });
        });
    }
    static fromYAML(yaml, registry = ProjectSerializer.defaultRegistry) {
        return __awaiter(this, void 0, void 0, function* () {
            return ProjectSerializer.fromJSON(safeLoad(yaml), registry);
        });
    }
    static createVisualization(template, data, project, registry = ProjectSerializer.defaultRegistry) {
        return registry.fromJSON('visualization', template, data, project);
    }
}
ProjectSerializer.defaultPlugins = [
    new DefaultPlugin(), new ActivityLogPlugin()
];
ProjectSerializer.defaultRegistry = new ObjectFactoryRegistry(ProjectSerializer.defaultPlugins);

/*
 * Public API Surface of @dvl-fw/core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ActivityLogDataSource, ActivityLogDataSourceFactory, ActivityLogPlugin, ActivityLogRawData, ActivityLogRawDataFactory, CSVTemplateProject, ColorScale, DataType, DefaultDataSource, DefaultDataSourceFactory, DefaultDataVariable, DefaultGraphicSymbol, DefaultGraphicSymbolFactory, DefaultGraphicVariable, DefaultGraphicVariableMapping, DefaultGraphicVariableMappingFactory, DefaultPlugin, DefaultProject, DefaultProjectFactory, DefaultRawData, DefaultRawDataFactory, DefaultRecordSet, DefaultRecordSetFactory, DefaultRecordStream, DefaultVisualization, DefaultVisualizationFactory, GraphicSymbolData, GraphicVariableType, ObjectFactoryRegistry, ProjectSerializer, ScaleType, SizeScale, Transient, areaSizeRange, areaSizeScale, areaSizeScaleNormQuantitative, colorScale, colorScaleNormQuantitative, colorScaleNormQuantitativeStroke, colorScaleReversedNormQuantitative, colorScaleReversedNormQuantitativeStroke, defaultStyles, extractPoint, fontSizeRange, fontSizeScale, fontSizeScaleNormQuantitative, formatNumber, formatYear, greyScale, greyScaleNormQuantitative, greyScaleNormQuantitativeStroke, norm0to100, parse, quantitativeTransparency, strokeWidthRange, strokeWidthScale, strokeWidthScaleNormQuantitative, ɵ0, ɵ1, ɵ2 };

